if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          // WEB-6294: the approved Figma keeps the label "ADD TO BAG" on a sold-out
          // variant (greyed + disabled) instead of swapping it to "Sold out".
          // Dawn's product-info.js passes window.variantStrings.soldOut into this
          // method; that file is an unprefixed Dawn original and the duplicate-and-
          // prefix rule bars editing it, so the label decision is enforced here.
          // Re-assert addToCart rather than merely skipping the write, so arriving
          // from an "Unavailable" variant does not leave that wording stuck.
          // MUST stay in step with snippets/sexyhair-buy-buttons.liquid, which bakes
          // the same choice in at render time.
          const soldOutText = window.variantStrings && window.variantStrings.soldOut;
          const unavailableText = window.variantStrings && window.variantStrings.unavailable;
          if (text && soldOutText && text === soldOutText) {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          } else if (text) {
            this.submitButtonText.textContent = text;
          }

          // WEB-6294 review: Dawn's product-info.js `setUnavailable()` returns
          // BEFORE it publishes PUB_SUB_EVENTS.variantChange, so a component that
          // only subscribes to that event never learns the customer picked an
          // option combination with no matching variant. <sexyhair-shop-at> then
          // keeps the previous variant's retailer panel on screen beside a
          // disabled button.
          //
          // setUnavailable calls this method first, so announcing the state here
          // is the earliest hook we own. A DOM event keeps the dependency
          // one-directional: this file announces, listeners opt in, and nothing
          // here needs to know they exist. Dawn's own file stays untouched.
          if (text && unavailableText && text === unavailableText) {
            this.dispatchEvent(
              new CustomEvent('sexyhair:variant-unavailable', {
                bubbles: true,
                detail: { sectionId: this.dataset.sectionId },
              }),
            );
          }
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
        this.syncSexyhairButtonClasses(disable);
      }

      // WEB-6148: sexyhair-buy-buttons.liquid bakes the gray look into class
      // names at Liquid render time (--disabled modifier + the shared sexyhair-cta--disabled
      // class, WEB-6429). Toggling the [disabled] attribute alone leaves
      // the button visually grayed out when switching from an OOS variant to an
      // in-stock one. Class lists must stay aligned with sexyhair-buy-buttons.liquid.
      syncSexyhairButtonClasses(disable) {
        if (!this.submitButton.classList.contains('sexyhair-buy-buttons__submit')) {
          if (this.submitButton.classList.contains('product-form__submit') && this.closest('.sexyhair-main-product')) {
            console.warn('[sexyhair] product-form.js: PDP submit button is missing the .sexyhair-buy-buttons__submit class — disabled-state class sync will no-op. Did you rename the class in sexyhair-buy-buttons.liquid without updating syncSexyhairButtonClasses?');
          }
          return;
        }

        // Class lists MUST mirror the enabled/disabled class branches rendered
        // by snippets/sexyhair-buy-buttons.liquid (WEB-6148): the button always
        // carries `sexyhair-cta`; these arrays swap the variant modifier.
        const enabledClasses = ['sexyhair-cta--red'];
        const disabledClasses = ['sexyhair-buy-buttons__submit--disabled', 'sexyhair-cta--disabled'];

        const [on, off] = disable
          ? [disabledClasses, enabledClasses]
          : [enabledClasses, disabledClasses];

        this.submitButton.classList.add(...on);
        this.submitButton.classList.remove(...off);
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    },
  );
}
